import { Button } from "components/ui/button";
import Toggle from "components/ui/toggle";
import { useEffect, useRef, useState } from "react";
import { SettingsSection } from "../components/settings-section";
import { useTranslation } from "react-i18next";
import { TripControlModal } from "../modals/trip-control-modal";
import { RebootDeviceModal } from "../modals/reboot-device-modal";
import { LanguageSelection } from "../components/language-selection";
import { TabContainer } from "../components/tab-container";
import { ScrollButtons } from "components/ui/scroll-buttons";
import { useStore } from "hooks/use-store";
import { Maximize, Minimize, Power, RotateCcw } from "lucide-react";

export const GeneralSettings = ({ title }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "settings",
  });

  const [showTripControlModal, setShowTripControlModal] = useState(false);
  const [showRebootModal, setShowRebootModal] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const refreshPage = () => {
    window.location.reload();
  };

  const [isFullscreen, setIsFullscreen] = useState(
    Boolean(document.fullscreenElement)
  );

  useEffect(() => {
    const onFullscreenChange = () =>
      setIsFullscreen(Boolean(document.fullscreenElement));

    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  const showScrollButtons = useStore((state) => state.showScrollButtons);
  const setShowScrollButtons = useStore((state) => state.setShowScrollButtons);

  return (
    <TabContainer ref={ref} title={title} className="space-y-2">
      <ScrollButtons containerRef={ref} className="absolute bottom-4 right-4" />
      <SettingsSection
        label={t("general_settings.language.title")}
        description={t("general_settings.language.description")}
        content={<LanguageSelection />}
      />
      <SettingsSection
        label={t("general_settings.scroll_buttons.title")}
        description={t("general_settings.scroll_buttons.description")}
        content={
          <Toggle
            enabled={showScrollButtons}
            setEnabled={(value) => setShowScrollButtons(value)}
          />
        }
      />
      <SettingsSection
        label={t("general_settings.fullscreen.title")}
        description={t("general_settings.fullscreen.description")}
        content={
          <Button onClick={toggleFullscreen} variant="secondary">
            {isFullscreen ? (
              <Minimize className="size-4 mr-2" />
            ) : (
              <Maximize className="size-4 mr-2" />
            )}
            {isFullscreen
              ? t("general_settings.fullscreen.exit_button")
              : t("general_settings.fullscreen.enter_button")}
          </Button>
        }
      />
      <SettingsSection
        label={t("general_settings.trip_control.title")}
        description={t("general_settings.trip_control.description")}
        content={
          <Button
            onClick={() => setShowTripControlModal(true)}
            variant="secondary"
          >
            {t("general_settings.trip_control.button")}
          </Button>
        }
      />
      <SettingsSection
        label={t("system_status.browser.refresh_title")}
        description={t("system_status.browser.refresh_description")}
        content={
          <Button onClick={refreshPage} variant="secondary">
            <RotateCcw className="size-4 mr-2" />
            {t("system_status.browser.refresh")}
          </Button>
        }
      />
      <SettingsSection
        label={t("general_settings.reboot_device.title")}
        description={t("general_settings.reboot_device.description")}
        content={
          <Button onClick={() => setShowRebootModal(true)} variant="secondary">
            <Power className="size-4 mr-2" />
            {t("general_settings.reboot_device.button")}
          </Button>
        }
      />
      <TripControlModal
        show={showTripControlModal}
        onClose={() => setShowTripControlModal(false)}
      />
      <RebootDeviceModal
        show={showRebootModal}
        onClose={() => setShowRebootModal(false)}
      />
    </TabContainer>
  );
};
